import t from '@rcm/i18n-client';
import { Form, InputTextField, SubmitButton } from 'forms';
import LoadingSpinner from 'loading-spinner/LoadingSpinner';
import React, { useCallback, useEffect, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import isNewPasswordValid from '../functions/isNewPasswordValid';
import setNewPasswordApi from '../functions/setNewPassword.client';

interface Props extends RouteComponentProps<{ userId: string; token: string; expirationTimestamp: string }> {}

enum OperationStatus {
  NotStarted = 'NOT_STARTED',
  Loading = 'LOADING',
  Done = 'DONE',
  InvalidFormData = 'INVALID_FORM_DATA',
  CannotSetNewPassword = 'CANNOT_SET_NEW_PASSWORD',
  CommunicationError = 'COMMUNICATION_ERROR',
  InvalidToken = 'INVALID_TOKEN',
}

const SetNewPassword = withRouter((props: Props) => {
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordAgain, setNewPasswordAgain] = useState('');
  const [operationStatus, setOperationStatus] = useState(OperationStatus.NotStarted);

  useEffect(() => {
    // If the token is expired, let the user know before they can enter a new password, as a convenience for the user
    if (parseInt(props.match.params.expirationTimestamp) < new Date().getTime()) {
      setOperationStatus(OperationStatus.InvalidToken);
    }
  }, [props.match.params.expirationTimestamp]);

  const handleSubmit = useCallback(() => {
    if (
      newPassword.length === 0 ||
      newPasswordAgain.length === 0 ||
      newPassword !== newPasswordAgain ||
      !isNewPasswordValid(newPassword)
    ) {
      setOperationStatus(OperationStatus.InvalidFormData);
      return;
    }
    (async () => {
      setOperationStatus(OperationStatus.Loading);
      const requestData = {
        userId: props.match.params.userId,
        token: props.match.params.token,
        password: newPassword,
      };
      try {
        await setNewPasswordApi(requestData);
      } catch (e) {
        if (e.response?.status === 422) {
          setOperationStatus(OperationStatus.CannotSetNewPassword);
          return;
        } else {
          setOperationStatus(OperationStatus.CommunicationError);
          return;
        }
      }
      setOperationStatus(OperationStatus.Done);
    })();
  }, [newPassword, newPasswordAgain, props.match.params.userId, props.match.params.token]);

  return (
    <div className="password-reset-request-reset-email">
      {operationStatus === OperationStatus.InvalidToken && (
        <div className="alert alert-warning" role="alert">
          {t('Password reset emails expire after one hour.' + ' Please call us if you need additional assistance.')}
        </div>
      )}
      {operationStatus === OperationStatus.CannotSetNewPassword && (
        <div className="alert alert-warning" role="alert">
          {t('Your password could not be reset.' + ' Please call us if you need additional assistance.')}
        </div>
      )}
      {operationStatus === OperationStatus.CommunicationError && (
        <div className="alert alert-warning" role="alert">
          {t('An error occurred. Please ensure you are connected to the internet and try again.')}
        </div>
      )}
      {operationStatus === OperationStatus.Done && (
        <>
          <div className="alert alert-success" role="alert">
            {t('Your password has been changed.')}
          </div>
          <a href="/login" className="btn btn-primary">
            {t('login')}
          </a>
        </>
      )}
      {operationStatus === OperationStatus.InvalidFormData && (
        <div className="alert alert-warning" role="alert">
          {t('Some information was missing or invalid on the form.' + ' Please check the form and try again.')}
        </div>
      )}
      {operationStatus === OperationStatus.Loading && <LoadingSpinner />}
      {![OperationStatus.Loading, OperationStatus.Done, OperationStatus.InvalidToken].includes(operationStatus) && (
        <Form onSubmit={handleSubmit}>
          <InputTextField
            label={t('New Password' + ' (' + t('letters and numbers only') + ')')}
            value={newPassword}
            onValueChange={setNewPassword}
            type="password"
          />
          <InputTextField
            label={t('Verify Password')}
            value={newPasswordAgain}
            onValueChange={setNewPasswordAgain}
            type="password"
          />
          <SubmitButton label={t('Reset Password')} className="pull-right" />
        </Form>
      )}
    </div>
  );
});

export default SetNewPassword;
